import React, { type MutableRefObject } from 'react';
import { Checkbox, Form, Input } from 'antd';
import type { Rule } from 'antd/es/form';
import { useIntl } from 'react-intl';

import type { Stripe } from '@stripe/stripe-js';

import { BillingAddressFields } from '../billing-address-fields';
import { FormField } from '../form-field';
import { IsolatedStripeElements } from '../isolated-stripe-elements';
import { PaymentDetailsSectionHeader } from '../payment-details-section-header';
import { SplitPayInFullChargeField } from '../split-pay-in-full-fields';
import { StripeCardFields } from '../stripe-card-fields';
import type { StripeCardElementsGroup } from '../stripe-card-fields';
import { usePaymentFormValidationRules } from '../../payment-form-validation-context';

import formStyles from '../../styles.module.scss';

interface FullPaymentDetailsSectionProps {
  showSplitPayInFull: boolean;
  certificateTotal: number;
  splitFullRules1: Rule[];
  splitFullRules2: Rule[];
  splitFullLastRef: MutableRefObject<'1' | '2'>;
  stripePromise: PromiseLike<Stripe | null> | null;
  billingAddressSameAsFirst: boolean | undefined;
  showSecondCard: boolean;
  cardOneSectionLabel: string;
  isSubmitting: boolean;
  setCard1Group: (group: StripeCardElementsGroup | null) => void;
  setCard2Group: (group: StripeCardElementsGroup | null) => void;
}

export function FullPaymentDetailsSection({
  showSplitPayInFull,
  certificateTotal,
  splitFullRules1,
  splitFullRules2,
  splitFullLastRef,
  stripePromise,
  billingAddressSameAsFirst,
  showSecondCard,
  cardOneSectionLabel,
  isSubmitting,
  setCard1Group,
  setCard2Group,
}: FullPaymentDetailsSectionProps): React.ReactElement {
  const intl = useIntl();
  const validationRules = usePaymentFormValidationRules();
  const namePlaceholder = intl.formatMessage({ id: 'certificates.payment.nameOnCardPlaceholder' });

  return (
    <section className={formStyles.paymentDetailsSection}>
      <PaymentDetailsSectionHeader />

      {showSplitPayInFull ? (
        <>
          <p className={formStyles.subsectionLabel}>
            {intl.formatMessage({ id: 'certificates.payment.firstPaymentSection' })}
          </p>
          <SplitPayInFullChargeField
            certificateTotal={certificateTotal}
            fieldName="splitAmount1"
            editedKey="1"
            rules={splitFullRules1}
            formItemClassName={formStyles.formItem}
            splitFullLastRef={splitFullLastRef}
          />
          <p className={formStyles.subsectionLabel}>
            {intl.formatMessage({ id: 'certificates.payment.card1Section' })}:
          </p>
          <FormField
            name="nameOnCard"
            label={intl.formatMessage({ id: 'certificates.payment.nameOnCard' })}
            variant="input"
            rules={validationRules.nameOnCard}
            className={formStyles.formItem}
          >
            <Input placeholder={namePlaceholder} />
          </FormField>
          <StripeCardFields isLoading={isSubmitting} onElementsReady={setCard1Group} />
          <BillingAddressFields variant="billing1" formItemClassName={formStyles.formItem} />

          <div className={formStyles.splitSecondPaymentBlock}>
            <p className={formStyles.subsectionLabel}>
              {intl.formatMessage({ id: 'certificates.payment.secondPaymentSection' })}
            </p>
            <SplitPayInFullChargeField
              certificateTotal={certificateTotal}
              fieldName="splitAmount2"
              editedKey="2"
              rules={splitFullRules2}
              formItemClassName={formStyles.formItem}
              splitFullLastRef={splitFullLastRef}
            />
            <p className={formStyles.subsectionLabel}>
              {intl.formatMessage({ id: 'certificates.payment.card2Section' })}:
            </p>
            <FormField
              name="nameOnCard2"
              label={intl.formatMessage({ id: 'certificates.payment.nameOnCard' })}
              variant="input"
              rules={validationRules.nameOnCard}
              className={formStyles.formItem}
            >
              <Input placeholder={namePlaceholder} />
            </FormField>
            {stripePromise ? (
              <IsolatedStripeElements stripe={stripePromise}>
                <StripeCardFields
                  fieldSuffix="2"
                  isLoading={isSubmitting}
                  onElementsReady={setCard2Group}
                />
              </IsolatedStripeElements>
            ) : null}
            <Form.Item
              name="billingAddressSameAsFirst"
              valuePropName="checked"
              className={formStyles.sameAsFirstCheckboxItem}
            >
              <Checkbox>
                {intl.formatMessage({ id: 'certificates.payment.sameAsFirstPayment' })}
              </Checkbox>
            </Form.Item>
            {!billingAddressSameAsFirst && (
              <BillingAddressFields variant="billing2" formItemClassName={formStyles.formItem} />
            )}
          </div>
        </>
      ) : (
        <>
          <p className={formStyles.subsectionLabel}>{cardOneSectionLabel}</p>
          <FormField
            name="nameOnCard"
            label={intl.formatMessage({ id: 'certificates.payment.nameOnCard' })}
            variant="input"
            rules={validationRules.nameOnCard}
            className={formStyles.formItem}
          >
            <Input placeholder={namePlaceholder} />
          </FormField>
          <StripeCardFields isLoading={isSubmitting} onElementsReady={setCard1Group} />

          {showSecondCard && stripePromise && (
            <>
              <p className={formStyles.subsectionLabel}>
                {intl.formatMessage({ id: 'certificates.payment.secondPaymentSection' })}
              </p>
              <FormField
                name="nameOnCard2"
                label={intl.formatMessage({ id: 'certificates.payment.nameOnCard' })}
                variant="input"
                rules={validationRules.nameOnCard}
                className={formStyles.formItem}
              >
                <Input placeholder={namePlaceholder} />
              </FormField>
              <IsolatedStripeElements stripe={stripePromise}>
                <StripeCardFields
                  fieldSuffix="2"
                  isLoading={isSubmitting}
                  onElementsReady={setCard2Group}
                />
              </IsolatedStripeElements>
            </>
          )}

          <BillingAddressFields variant="default" formItemClassName={formStyles.formItem} />
        </>
      )}
    </section>
  );
}
